// ``useThreads`` used to live here — it wrapped TipTap Pro's Comments
// extension via ``provider.watchThreads`` / ``editor.chain().setThread``.
// Native comments now run through ``src/util/commentsApi.js`` against
// Django's ``/api/comments/`` endpoints, rendered by
// ``components/WriteProject/CommentsSidebar.jsx`` — no Yjs-threaded
// mark, no cloud dependency.

import { useMemo } from "react";
import { apiClient, SESSION_CLEARED_EVENT } from "../util/apiClient";
import { getAccessToken } from "../util/auth";
import { useAuth } from "../auth/AuthContext";
import { logger } from "../util/logger";

const colors = [
  "#958DF1",
  "#F98181",
  "#FBBC88",
  "#FAF594",
  "#70CFF8",
  "#94FADB",
  "#B9F18D",
];
const names = [
  "Lea Thompson",
  "Cyndi Lauper",
  "Tom Cruise",
  "Madonna",
  "Jerry Hall",
  "Joan Collins",
  "Winona Ryder",
  "Christina Applegate",
  "Alyssa Milano",
  "Molly Ringwald",
  "Ally Sheedy",
  "Debbie Harry",
  "Olivia Newton-John",
  "Elton John",
  "Michael J. Fox",
  "Axl Rose",
  "Emilio Estevez",
  "Ralph Macchio",
  "Rob Lowe",
  "Jennifer Grey",
  "Mickey Rourke",
  "John Cusack",
  "Matthew Broderick",
  "Justine Bateman",
  "Lisa Bonet",
];

const getRandomElement = (list) =>
  list[Math.floor(Math.random() * list.length)];

const getRandomColor = () => getRandomElement(colors);
const getRandomName = () => getRandomElement(names);

// Collab JWT fetch. Pre-migration this also posted to
// ``/api/docnames_allowed/find/`` and ``/api/docnames_readonly/find/`` to
// pre-compute the caller's ACL client-side — that was Firebase-era and is
// redundant now that ``services/jwt_auth.py`` derives permissions from the
// authenticated caller server-side. Keep the fetch **lazy** and **guarded**:
// any component that imports ``useUser`` used to eagerly fire this at
// module load, which hammered the console with CORS errors on every page
// mount before the user had even signed in.
let collabTokenPromise = null;

// Drop the memoised token whenever ``apiClient`` wipes the session
// (explicit sign-out OR silent-refresh failure). Without this the
// provider would keep using a stale collab JWT across login cycles.
if (typeof window !== "undefined") {
  window.addEventListener(SESSION_CLEARED_EVENT, () => {
    collabTokenPromise = null;
  });
}

export async function fetchCollabToken() {
  // HocuspocusProvider retries the WebSocket on every failure. If the
  // editor mounts without an access token (boot-time session check not
  // finished, or the user landed here signed out), firing the POST
  // would guarantee a 401 on every retry and spam the Django log. Skip
  // the network call in that case and return an empty string — the
  // provider then surfaces ``onAuthenticationFailed`` instead of
  // looping through 401s.
  if (!getAccessToken()) return "";
  if (collabTokenPromise) return collabTokenPromise;
  collabTokenPromise = apiClient
    .post("/api/jwt/create_jwt/", new FormData())
    .then((r) => r.data?.jwt ?? "")
    .catch((err) => {
      logger.warn("collab_token_fetch_failed", err?.message);
      // Allow a future retry instead of caching the failure.
      collabTokenPromise = null;
      // Return an empty string rather than ``null`` so
      // ``HocuspocusProvider``'s ``writeAuthentication`` doesn't hit a
      // ``str.length`` crash on the uninitialised token.
      return "";
    });
  return collabTokenPromise;
}

// Per-tab collab cursor colour — picked once per page load so the
// caret colour stays stable across remounts. Collab cursor doesn't
// need to track auth state, so a module-level const is fine here.
export const userColor = getRandomColor();

// ``useUser`` is a real hook now — derives the live user from
// ``useAuth()`` so a sign-in / sign-out updates every consumer
// without a page reload. Earlier this returned module-level
// constants captured at import time, which produced two bugs:
//   * ``userName`` fell back to a random celebrity ("Madonna",
//     "Tom Cruise", …) when sessionStorage hadn't yet been
//     populated by ``Navigation``'s mirror effect, so the welcome
//     line on /browse showed the wrong name on first load.
//   * ``userAvatar`` was always whatever was in sessionStorage at
//     module evaluation — empty for fresh sign-ins, stale across
//     account switches.
// The random celebrity fallback is kept for the collab cursor only
// (signed-out visitors loading the editor) — it's a display-name
// crutch for cursor presence, not a UI label, so a randomised
// stand-in there is fine.
export const useUser = () => {
  const { user } = useAuth();
  return useMemo(() => {
    const fallbackName = user?.email || getRandomName();
    return {
      name: user?.username || user?.fullname || fallbackName,
      // ``username`` is also exposed verbatim so callers building
      // profile URLs can prefer it via ``profilePath`` without
      // re-implementing the fallback chain.
      username: user?.username || "",
      email: user?.email || "",
      color: userColor,
      avatar: user?.picture || "",
    };
  }, [user]);
};
